/**
 * Tags group expenses. Trips are a kind of tag too, but they live in the trips table (dates, places,
 * travelers) and an expense has at most one; the tags here are free-form and an expense can carry many.
 */
export type TagKind = 'topic' | 'recurring';

/** Who put a tag on an expense. */
export type TagSource = 'user' | 'ai' | 'rule';

export interface Tag {
  id: string;
  name: string;
  kind: TagKind;
  color: string | null;
  transactionCount: number;
  /** Spending in USD across the tagged expenses (net of refunds). */
  totalUsd: number;
  createdAt: string;
}

/** A tag as it appears on one expense. */
export interface TransactionTag {
  id: string;
  name: string;
  kind: TagKind;
  color: string | null;
  source: TagSource;
}

export const TAG_KINDS: TagKind[] = ['topic', 'recurring'];
export const TAG_NAME_MAX = 60;

/** Trims and collapses whitespace; returns null when nothing usable is left. */
export function cleanTagName(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  const cleaned = name.replace(/\s+/g, ' ').trim();
  return cleaned.length > 0 && cleaned.length <= TAG_NAME_MAX ? cleaned : null;
}

export const TAG_COLORS = ['#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6', '#06b6d4', '#ef4444', '#84cc16', '#0ea5e9', '#d946ef'];

/** A stable color for a tag that has none, so the same name always looks the same. */
export function defaultTagColor(name: string): string {
  let hash = 0;
  for (const ch of tagNameKey(name)) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return TAG_COLORS[hash % TAG_COLORS.length];
}

/** Key that makes tag names unique per user regardless of case and spacing. */
export function tagNameKey(name: string): string {
  return name.replace(/\s+/g, ' ').trim().toLowerCase();
}
